import { getLogger } from "@expert/logging";
import { ConfettiAnimation } from "@expert/sales-tools";
import { Container, Flex } from "@mantine/core";
import { useEffect } from "react";
import { withErrorBoundary } from "react-error-boundary";

import type { ExpertUniversitySimulatedVoiceTask } from "@/expert-university";
import type { AgentActivity, AgentSdk, CustomerSession, TrainingTask, WithoutCustomerSession } from "@/sdk";

import { ChatManager } from "@/components/chat";
import styles from "@/components/workspace/workspace.module.css";
import { env } from "@/config";
import { useExpertUniversityWebsocket } from "@/expert-university/hooks/useExpertUniversityWebsocket";
import { NotificationContainer } from "@/notification/NotificationContainer";
import { MicroLearning } from "@/sales-tools";
import { Announcement } from "@/sales-tools/challenges/Announcement";
import { createFallbackRender } from "@/shared-utils";
import { Troubleshooting } from "@/troubleshoot";
import { APP_START, trackTimeElapsed } from "@/utils/trackTimeElapsed";

import { VoiceWorkspaceContent } from "./VoiceWorkspaceContent";

// Fallback logger for error boundaries (created at module level for stability)
const fallbackLogger = getLogger({ module: "WorkspaceContent" });

const MicroLearningModalHandled = withErrorBoundary(MicroLearning, {
    fallbackRender: createFallbackRender(fallbackLogger),
});
const AnnouncementModalHandled = withErrorBoundary(Announcement, {
    fallbackRender: createFallbackRender(fallbackLogger),
});

type WorkspaceContentProps = {
    activeExpertUniversityTask: ExpertUniversitySimulatedVoiceTask | null;
    activeTrainingTask: TrainingTask | null;
    callControlsEnabled: boolean;
    hasActiveVoiceTask: boolean;
    hasCallbackWaiting: boolean;
    isOutboundEnabled: boolean | undefined;
    isRealCustomerPanelEnabledForTraining: boolean | undefined;
    challengeEnabled: boolean;
    currentActivity: AgentActivity | undefined;
    customerPanelEnabled: boolean;
    customerSession: boolean;
    hasCallEnded: boolean;
    microLearningV2Enabled: undefined | boolean;
    showRealtimeTranscript: boolean;
    sdk: AgentSdk;
    session: CustomerSession | WithoutCustomerSession;
    showExpertUniversity: undefined | boolean;
    troubleshooting: boolean;
};

export function WorkspaceContent({
    activeExpertUniversityTask,
    activeTrainingTask,
    callControlsEnabled,
    hasActiveVoiceTask,
    hasCallbackWaiting,
    isOutboundEnabled,
    isRealCustomerPanelEnabledForTraining,
    challengeEnabled,
    currentActivity,
    customerPanelEnabled,
    customerSession,
    hasCallEnded,
    microLearningV2Enabled,
    sdk,
    session: { startedTimestamp, id: sessionId },
    showExpertUniversity,
    showRealtimeTranscript,
    troubleshooting,
}: WorkspaceContentProps) {
    useExpertUniversityWebsocket();

    useEffect(() => {
        trackTimeElapsed(APP_START, "timeToPanelsRendered");
    }, []);

    return (
        <Flex flex={1}>
            {env.channel === "chat" ? (
                <ChatManager />
            ) : (
                <VoiceWorkspaceContent
                    activeExpertUniversityTask={activeExpertUniversityTask}
                    activeTrainingTask={activeTrainingTask}
                    callControlsEnabled={callControlsEnabled}
                    customerPanelEnabled={customerPanelEnabled}
                    customerSession={customerSession}
                    hasActiveVoiceTask={hasActiveVoiceTask}
                    hasCallbackWaiting={hasCallbackWaiting}
                    isOutboundEnabled={isOutboundEnabled}
                    isRealCustomerPanelEnabledForTraining={isRealCustomerPanelEnabledForTraining}
                    logger={fallbackLogger}
                    sessionId={sessionId}
                    showExpertUniversity={showExpertUniversity}
                    showRealtimeTranscript={showRealtimeTranscript}
                    startedTimestamp={startedTimestamp}
                />
            )}

            {/* Modals go outside Panels */}

            {/* Modals */}
            {(challengeEnabled || microLearningV2Enabled) &&
            hasCallEnded &&
            !customerSession &&
            currentActivity === sdk.availableAgentActivity ? (
                <>
                    <AnnouncementModalHandled />
                    <MicroLearningModalHandled />
                </>
            ) : null}

            {/* Modal */}
            {(challengeEnabled || microLearningV2Enabled) && currentActivity === sdk.defaultAgentActivity && (
                <AnnouncementModalHandled />
            )}

            {/* Modal */}
            {troubleshooting ? <Troubleshooting /> : null}

            {/* Notifications are global and outside of Panels */}
            <NotificationContainer />

            <Container className={styles.confettiContainer} key={sessionId}>
                <ConfettiAnimation />
            </Container>
        </Flex>
    );
}
